// this is a temporary file to migrate all editor atoms to typescript
// once we have all atoms migrated, we can remove this file and place atoms in editorAtoms.ts

import {
  atom,
  atomFamily,
  DefaultValue,
  selector,
  selectorFamily,
} from 'recoil'
import type { RecoilState, RecoilValueReadOnly } from 'recoil'
import { default as _set } from 'lodash/set'
import { default as _get } from 'lodash/get'
import { produce } from 'immer'
import type {
  Locale,
  Content,
  GetGlobalServiceInformationTemplatesNormalized,
  AlignmentPoints,
  AlignmentVisibility,
  BaseItem,
  ImageItem,
} from '@messenger/types'
import type { EditorOrderPageDetailsNormalized } from '@messenger/types'
import { LOCALE } from '@messenger/consts'
import { withinBoundingBox } from '@/utils/editor/withinBoundingBox'
import {
  layoutMarginsOf,
  type LayoutMargins,
} from '@/utils/editor/layoutMargins'
import { draggingSelectionAtom, selectionRangeAtom } from './editorAtoms'

type BulkProcessingProgress = {
  processedCount: number
  totalCount: number
  currentAction: string
}

export const pageSetLocaleAtom = atom<Locale>({
  key: 'pageSetLocale',
  default: LOCALE.EN,
})

export const editorPagesAtom = atom<Content[]>({
  key: 'editorPages',
  default: [],
})

export const alignmentPointsAtom = atom<AlignmentPoints>({
  key: 'alignmentPointsObj',
  default: {
    vertical: [],
    horizontal: [],
  },
})

export const alignmentVisibilityAtom = atom<AlignmentVisibility>({
  key: 'alignmentVisibilityObj',
  default: {
    verticalSnap: null,
    horizontalSnap: null,
    verticalVisible: [],
    horizontalVisible: [],
  },
})

export const baseLayoutAtom = atom<
  EditorOrderPageDetailsNormalized['layout'] | null
>({
  key: 'baseLayout',
  default: null,
})

export const baseLayoutMarginsSelector = selector<LayoutMargins | null>({
  key: 'baseLayoutMargins',
  get: ({ get }) => layoutMarginsOf(get(baseLayoutAtom)),
})

export const multiSelectModeAtom = atom<boolean>({
  key: 'multiSelectMode',
  default: false,
})

export const commitMultiSelectPositionsAtom = atom<number>({
  key: 'commitMultiSelectPositions',
  default: 0,
})

export const showServiceTemplatesSwapMenuAtom = atom<boolean>({
  key: 'showServiceTemplatesSwapMenu',
  default: false,
})

export const swapServiceTemplateSignalAtom = atom<number>({
  key: 'swapServiceTemplateSignal',
  default: 0,
})

export const serviceInformationTemplatesAtom = atom<
  GetGlobalServiceInformationTemplatesNormalized[]
>({
  key: 'serviceInformationTemplates',
  default: [],
})

export const isBulkProcessingAtom = atom<boolean>({
  key: 'isBulkProcessing',
  default: false,
})

// Whether the editor shows the prompt that the page set was turned over to
// Lotus text boxes after this tab loaded it (`EditorPageSetTurnedOverModal`):
// raised by a Save or a turnover the server refused with the turned-over code.
export const editorPageSetTurnedOverPromptAtom = atom<boolean>({
  key: 'editorPageSetTurnedOverPrompt',
  default: false,
})

// Raised when a save from an edit-link tab is refused because the tab no
// longer holds access to the order (`isEditAccessLostError`); one prompt per
// save gesture, however many pages were refused.
export const editorAccessLostPromptAtom = atom<boolean>({
  key: 'editorAccessLostPrompt',
  default: false,
})

// The key (`triggerUseEffect`) of the last editor load that ended without
// loading, set by Editor.js's load effect when it fails; null until one does.
// A load that fails never reports the editor ready, so whatever waits for the
// ready of a load it started reads this to stop waiting.
export const editorLoadFailedKeyAtom = atom<number | null>({
  key: 'editorLoadFailedKey',
  default: null,
})

// True while a Save is in flight (from update() entry to save() completion).
// Gates the Save button and page-nav handlers so mid-save interactions can't
// trigger another saveItems against a different selectedPage.
export const isSavingAtom = atom<boolean>({
  key: 'isSaving',
  default: false,
})

// Whether the backend this editor loaded from has the route that takes one
// version for a whole Save. A client that hears nothing at load is talking to
// a release without it and keeps taking a version per page write.
export const editorSaveVersionSupportedAtom = atom<boolean>({
  key: 'editorSaveVersionSupported',
  default: false,
})

// True once Page.js's loadInitialData has hydrated the current page's items into
// itemIdAtom. False on mount and during the editor re-init window (a layout swap
// / page-set reload). The save guard reads this to tell a transient un-hydrated
// empty page apart from a user intentionally clearing a page.
export const isEditorReadyAtom = atom<boolean>({
  key: 'isEditorReady',
  default: false,
})

// `product.orderPageId` of the page whose items currently populate itemIdAtom /
// itemAtomFamily. Page.js's loadInitialData stamps it as it hydrates.
//
// isEditorReadyAtom is not enough on its own: it stays true across page
// navigation (only useResetEditorAtoms clears it), so during the window between
// a selectedPage change committing and the new Page's hydration effect running,
// the canvas still holds the PREVIOUS page's items while writes already target
// the new page. Any items write in that window clones page N-1 onto page N.
// Comparing this stamp against the write target closes the window.
export const canvasPageIdAtom = atom<string | null>({
  key: 'canvasPageId',
  default: null,
})

/**
 * The `currentAction` a page set's turnover blocks the editor under, so the
 * bulk-processing overlay shows the turnover's copy instead of the image
 * actions' count.
 */
export const BULK_PROCESSING_TURNOVER_ACTION = 'page-set-turnover'

export const bulkProcessingProgressAtom = atom<BulkProcessingProgress>({
  key: 'bulkProcessingProgress',
  default: {
    processedCount: 0,
    totalCount: 0,
    currentAction: '',
  },
})

// This is for testing purposes on behalf of autosizing hook
export const revealWrapperHeightAtom = atom<boolean>({
  key: 'revealWrapperHeight',
  default: false,
})

// Canonical names of fonts used across the current layout's text items,
// surfaced as a section at the top of the customFontSelect dropdown (#2435).
export const layoutUsedFontsAtom = atom<string[]>({
  key: 'layoutUsedFonts',
  default: [],
})

/**
 * Coordinates and sizes admit strings because saved page JSON from older
 * orders stores them that way, and the readers parseInt.
 */
export type EditorItem = {
  id?: string
  type?: string
  x?: number | string
  y?: number | string
  z?: number
  width?: number | string
  height?: number | string
  assetLocked?: boolean
  maintainAspectRatio?: boolean
  hideInPdf?: boolean
  manipulated?: BaseItem['manipulated']
  edits?: BaseItem['edits']
  properties?: { subtype?: string; caseImage?: unknown }
  image?: {
    url?: string
    editedUrl?: string
    edits?: ImageItem['image']['edits']
  }
  cropLeft?: number
  cropRight?: number
  cropTop?: number
  cropBottom?: number
  text?: { value?: string | null } | null
}

export const itemAtomFamily = atomFamily<EditorItem, string>({
  key: 'itemAtoms',
  default: {},
})

export const itemIdAtom = atom<string[]>({
  key: 'itemIds',
  default: [],
})

export const watermarkAtomFamily = atomFamily<EditorItem, string>({
  key: 'watermarkAtoms',
  default: {},
})

export const selectedItemsAtom = atom<string[]>({
  key: 'itemsSelectedInEditor',
  default: [],
})

export const selectedItemAtom: RecoilState<string> = selector<string>({
  key: 'itemSelectedInEditor',
  get: ({ get }) => {
    const selectedItems = get(selectedItemsAtom)
    return selectedItems.length === 1 ? selectedItems[0] : ''
  },
  set: ({ set }, value) =>
    set(
      selectedItemsAtom,
      value instanceof DefaultValue || value === '' ? [] : [value],
    ),
})

export const controlledXAtom = atom<number | null>({
  key: 'controlledX',
  default: null,
})
export const controlledYAtom = atom<number | null>({
  key: 'controlledY',
  default: null,
})

export const isItemSelectedSelector = selectorFamily<boolean, string>({
  key: 'isItemSelected',
  get:
    (itemId) =>
    ({ get }) =>
      get(selectedItemsAtom).includes(itemId),
})

export const isSoleSelectionSelector = selectorFamily<boolean, string>({
  key: 'isSoleSelection',
  get:
    (itemId) =>
    ({ get }) => {
      const selected = get(selectedItemsAtom)
      return selected.length === 1 && selected[0] === itemId
    },
})

export const selectionIsSoleSelector = selector<boolean>({
  key: 'selectionIsSole',
  get: ({ get }) => get(selectedItemsAtom).length <= 1,
})

const numberOrZero = (value: unknown): number => {
  const parsed = typeof value === 'string' ? parseInt(value, 10) : value
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : 0
}

/**
 * Whether the marquee being dragged on the canvas covers this item's centre.
 * An item that the marquee never reaches keeps reading `false`, so a click on
 * empty canvas — which arms the marquee at a point — re-renders nothing.
 */
export const marqueeCoversItemSelector = selectorFamily<boolean, string>({
  key: 'marqueeCoversItem',
  get:
    (itemId) =>
    ({ get }) => {
      if (!get(draggingSelectionAtom)) return false
      const item = get(itemAtomFamily(itemId)) as {
        x?: unknown
        y?: unknown
        width?: unknown
        height?: unknown
      } | null
      if (!item) return false
      return withinBoundingBox(
        numberOrZero(item.x),
        numberOrZero(item.y),
        numberOrZero(item.width),
        numberOrZero(item.height),
        get(selectionRangeAtom) as {
          top: number
          right: number
          left: number
          bottom: number
        },
      )
    },
})

export const pagePaddingJsonSelector = selectorFamily<string, number>({
  key: 'pagePaddingJson',
  get:
    (pageIndex) =>
    ({ get }) =>
      JSON.stringify(get(editorPagesAtom)[pageIndex]?.product?.padding ?? null),
})

export type GroupDragOffset = { x: number | null; y: number | null }

export const groupDragOffsetSelector = selectorFamily<
  GroupDragOffset | null,
  string
>({
  key: 'groupDragOffset',
  get:
    (itemId) =>
    ({ get }) => {
      if (!get(selectedItemsAtom).includes(itemId)) return null
      const x = get(controlledXAtom)
      const y = get(controlledYAtom)
      return x === null && y === null ? null : { x, y }
    },
})

export const unsavedChangesAtom = atom<boolean>({
  key: 'unsavedChanges',
  default: false,
})

type SelectedItemsSelector = (
  param?: undefined,
) => RecoilValueReadOnly<EditorItem[] | null>

export const itemsSelector: SelectedItemsSelector = selectorFamily<
  EditorItem[] | null,
  undefined
>({
  key: 'itemsSelector',
  get:
    () =>
    ({ get }) => {
      const selectedItems = get(selectedItemsAtom)
      if (selectedItems.length === 0) return null
      return selectedItems.map((id) => get(itemAtomFamily(id)))
    },
})

export type ItemSelectorParam = {
  id: string
  path?: string
  atomFamily?: string
  annotation?: boolean
}

type ItemFieldSelector = <TField = unknown>(
  param: ItemSelectorParam,
) => RecoilState<TField>

export const itemSelector = selectorFamily<unknown, ItemSelectorParam>({
  key: 'itemSelector',
  get:
    ({ id, path, atomFamily }) =>
    ({ get }) => {
      const atom =
        atomFamily === 'watermark' ? watermarkAtomFamily : itemAtomFamily
      const value = get(atom(id))
      if (!value) return null
      if (path) {
        let newValue = _get(value, path)
        if (path.includes('opacity')) newValue = newValue * 100
        return newValue
      }
      return value
    },
  set:
    ({ id, path, atomFamily, annotation }) =>
    ({ set, get }, newValue) => {
      const atom =
        atomFamily === 'watermark' ? watermarkAtomFamily : itemAtomFamily
      const value = get(atom(id))
      if (!value) return null
      if (path!.includes('opacity')) newValue = (newValue as number) / 100
      const newField = produce(value, (draft) => {
        _set(draft, path!, newValue)
      })
      set(atom(id), newField)
      if (!annotation) set(unsavedChangesAtom, true)
    },
}) as ItemFieldSelector

export const editorClipboardAtom = atom<EditorItem[]>({
  key: 'editorClipboard',
  default: [],
})
